import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Search, Sparkles, Copy as CopyIcon, Eye, Crown, Star, ShieldCheck, Trash2, Filter } from "lucide-react";

const SORTS = [
  { id: "popular", label: "Popular" },
  { id: "conversion", label: "Top Converting" },
  { id: "recent", label: "Recent" },
];

export default function Marketplace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ templates: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [tab, setTab] = useState("browse"); // browse | mine
  const [mine, setMine] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace", { params: { search, category, sort } });
      setData(r.data);
    } catch {
      toast.error("Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  };
  const loadMine = async () => {
    try {
      const r = await api.get("/marketplace/mine");
      setMine(r.data.templates);
    } catch { /* ignore: marketplace/mine is best-effort */ }
  };
  useEffect(() => { load(); loadMine(); }, [category, sort]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const clone = async (tpl) => {
    setBusyId(tpl.id);
    try {
      const r = await api.post(`/marketplace/${tpl.id}/clone`);
      toast.success(`"${tpl.title}" cloned to your library`);
      navigate(`/builder/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clone failed");
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeature = async (tpl) => {
    const next = tpl.status === "featured" ? "approved" : "featured";
    try {
      await api.patch(`/marketplace/${tpl.id}`, { status: next });
      toast.success(next === "featured" ? "Featured" : "Unfeatured");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setHidden = async (tpl) => {
    try {
      await api.patch(`/marketplace/${tpl.id}`, { status: "hidden" });
      toast.success("Hidden");
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const removeMine = async (tpl) => {
    if (!window.confirm(`Remove "${tpl.title}" from the marketplace?`)) return;
    try {
      await api.delete(`/marketplace/${tpl.id}`);
      toast.success("Removed");
      loadMine(); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="label-cap mb-2">// Community templates</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Marketplace</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl">High-converting templates published by Pro marketers. Clone with one click — fully editable in your account.</p>
      </div>

      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("browse")} className={`px-4 py-2 border-2 border-black font-bold text-sm ${tab === "browse" ? "bg-black text-white" : "bg-white"}`} data-testid="tab-browse">Browse</button>
        <button onClick={() => setTab("mine")} className={`px-4 py-2 border-2 border-black font-bold text-sm ${tab === "mine" ? "bg-black text-white" : "bg-white"}`} data-testid="tab-mine">My Published ({mine.length})</button>
      </div>

      {tab === "browse" ? (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <form onSubmit={onSearch} className="flex gap-2 flex-1 min-w-[260px]">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, tags, description…" className="input-brutal flex-1" data-testid="mkt-search" />
              <button type="submit" className="btn-brutal-outline !px-3" data-testid="mkt-search-btn"><Search size={16} /></button>
            </form>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-brutal !w-auto" data-testid="mkt-category">
              <option value="all">All categories</option>
              {data.categories.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <div className="flex border-2 border-black">
              {SORTS.map((s, i) => (
                <button key={s.id} onClick={() => setSort(s.id)}
                  className={`px-3 py-2 font-bold text-sm ${i > 0 ? "border-l-2 border-black" : ""} ${sort === s.id ? "bg-[#FF4F00] text-white" : "bg-white"}`}
                  data-testid={`mkt-sort-${s.id}`}>{s.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-zinc-500">Loading…</div>
          ) : data.templates.length === 0 ? (
            <div className="border-2 border-black bg-zinc-50 p-12 text-center">
              <Sparkles className="mx-auto mb-3 text-[#FF4F00]" size={32} />
              <div className="font-display font-black text-2xl mb-2">No templates yet</div>
              <p className="text-zinc-600">Be the first to publish a winning template — Pro users earn visibility + credibility.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.templates.map((tpl) => (
                <TemplateCard
                  key={tpl.id} tpl={tpl}
                  onClone={() => clone(tpl)}
                  busy={busyId === tpl.id}
                  isAdmin={isAdmin}
                  onToggleFeature={() => toggleFeature(tpl)}
                  onHide={() => setHidden(tpl)}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div>
          {mine.length === 0 ? (
            <div className="border-2 border-black bg-zinc-50 p-10 text-center">
              <p className="text-zinc-600 mb-4">You haven't published any templates yet.</p>
              <p className="text-sm text-zinc-500">Publish a winning lead magnet from the Builder. Pro feature.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {mine.map((tpl) => (
                <TemplateCard
                  key={tpl.id} tpl={tpl} mine
                  onRemove={() => removeMine(tpl)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}

function TemplateCard({ tpl, onClone, busy, isAdmin, onToggleFeature, onHide, mine, onRemove }) {
  const featured = tpl.status === "featured";
  const hidden = tpl.status === "hidden";
  const high = tpl.conversion_rate >= 25;
  return (
    <div className={`card-brutal !p-0 overflow-hidden flex flex-col ${featured ? "border-[#FF4F00]" : ""}`} data-testid={`mkt-card-${tpl.id}`}>
      <div className="aspect-[4/3] bg-zinc-100 border-b-2 border-black overflow-hidden relative">
        {tpl.thumbnail_url ? (
          <img src={tpl.thumbnail_url} alt={tpl.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200">
            <span className="font-display font-black text-3xl text-zinc-400">{tpl.title.slice(0, 2).toUpperCase()}</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
          {featured && <span className="px-2 py-1 text-xs font-bold bg-[#FF4F00] text-white border-2 border-black flex items-center gap-1"><Star size={10} fill="white" /> FEATURED</span>}
          {high && <span className="px-2 py-1 text-xs font-bold bg-black text-white border-2 border-black">{tpl.conversion_rate}% CVR</span>}
          {hidden && <span className="px-2 py-1 text-xs font-bold bg-zinc-700 text-white border-2 border-black">HIDDEN</span>}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="label-cap mb-1">{tpl.category}</div>
        <h3 className="font-display font-bold text-lg mb-2 line-clamp-2">{tpl.title}</h3>
        <p className="text-xs text-zinc-500 mb-2">by @{tpl.author_handle}</p>
        <p className="text-sm text-zinc-600 mb-3 flex-1 line-clamp-3">{tpl.description}</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {(tpl.tags || []).slice(0, 4).map((t) => (
            <span key={t} className="px-2 py-0.5 text-xs bg-zinc-100 border border-zinc-300">#{t}</span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
          <span className="flex items-center gap-1"><Eye size={12} /> {tpl.view_count || 0}</span>
          <span className="flex items-center gap-1"><CopyIcon size={12} /> {tpl.clone_count || 0} clones</span>
          {!high && tpl.conversion_rate > 0 && <span>· {tpl.conversion_rate}% CVR</span>}
        </div>
        {mine ? (
          <div className="flex gap-2">
            <span className="flex-1 px-3 py-2 text-xs font-bold border-2 border-black bg-zinc-50 text-center">{(tpl.status || "approved").toUpperCase()}</span>
            <button onClick={onRemove} className="btn-brutal-outline !px-3 !py-2" title="Remove" data-testid={`mkt-remove-${tpl.id}`}><Trash2 size={14} /></button>
          </div>
        ) : (
          <>
            <button onClick={onClone} disabled={busy} className="btn-brutal w-full mt-1" data-testid={`mkt-clone-${tpl.id}`}>
              <CopyIcon size={14} /> {busy ? "Cloning…" : "Clone & Edit"}
            </button>
            {isAdmin && (
              <div className="flex gap-2 mt-2">
                <button onClick={onToggleFeature} className="btn-brutal-outline !px-2 !py-1.5 text-xs flex-1" data-testid={`mkt-feature-${tpl.id}`}>
                  <Crown size={12} /> {featured ? "Unfeature" : "Feature"}
                </button>
                <button onClick={onHide} className="btn-brutal-outline !px-2 !py-1.5 text-xs flex-1" data-testid={`mkt-hide-${tpl.id}`}>
                  <ShieldCheck size={12} /> {hidden ? "Hidden" : "Hide"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
